import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Carousel } from 'antd';
import React, { useRef, useState } from 'react';
import { BannerItem } from '../../constants/types/banner-type';

/**
 * Antd Carousel 컴포넌트
 * @description 젭스 메인에서 사용되는 material-tailwind 캐러셀 컴포넌트와 동일한 디자인의 캐러셀 컴포넌트
 * @description 가장 큰 높이의 이미지에 캐러셀, 카드 높이가 맞춰짐
 * @param bannerItems 배너 이미지 리스트
 * @returns 캐러셀 컴포넌트
 */

interface AntdCarouselProps {
  bannerItems: BannerItem[];
  loading: boolean;
  handleBannerClick: (banner: BannerItem) => void;
}

const AntdCarousel = ({
  bannerItems,
  loading,
  handleBannerClick,
}: AntdCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<any>(null);
  const handlePrev = () => {
    carouselRef.current?.prev();
  };

  const handleNext = () => {
    carouselRef.current?.next();
  };

  // FIXME: 여기에 디폴트 배너를 하나 넣어두는게 어떤지?
  // bannerItems가 비어있으면 렌더링하지 않음
  if (!bannerItems || bannerItems.length === 0 || loading) {
    // console.log('AntdCarousel - returning null because:', {
    //   bannerItems: !!bannerItems,
    //   length: bannerItems?.length,
    //   loading,
    // });
    return null;
  }

  return (
    <div className="relative lg:col-span-2">
      <Carousel
        ref={carouselRef}
        autoplay={true}
        dots={false}
        arrows={false}
        beforeChange={(from, to) => setCurrentSlide(to)}
      >
        {bannerItems.map((item, index) => (
          <AntdCarouselItem
            key={index.toString()}
            item={item}
            handleBannerClick={handleBannerClick}
          />
        ))}
      </Carousel>

      {/* Material-tailwind 스타일의 커스텀 화살표 */}
      <div className="hidden xl:block">
        <button
          onClick={handlePrev}
          className="absolute left-8 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-md transition-all hover:shadow-lg"
        >
          <ChevronLeftIcon className="h-6 w-6 text-white" strokeWidth={3} />
        </button>
      </div>

      <div className="hidden xl:block">
        <button
          onClick={handleNext}
          className="absolute right-8 top-1/2 flex h-10 w-10 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full shadow-md transition-all hover:shadow-lg"
        >
          <ChevronRightIcon className="h-6 w-6 text-white" strokeWidth={3} />
        </button>
      </div>

      {/* Material-tailwind 스타일의 커스텀 네비게이션 */}
      <div className="absolute bottom-4 left-2/4 flex -translate-x-2/4 gap-2">
        {bannerItems.map((_, i) => (
          <span
            key={i}
            className={`block h-2 cursor-pointer rounded-full shadow-md transition-all content-[''] ${
              currentSlide === i
                ? 'w-4 bg-white'
                : 'w-2 bg-white/50 hover:bg-white/80'
            }`}
            onClick={() => {
              carouselRef.current?.goTo(i);
            }}
          />
        ))}
      </div>
    </div>
  );
};

const AntdCarouselItem = ({
  item,
  handleBannerClick,
}: {
  item: BannerItem;
  handleBannerClick: (banner: BannerItem) => void;
}) => {
  return (
    <div onClick={() => handleBannerClick(item)} className="cursor-pointer">
      {/* PC 배너 이미지 */}
      <div className="hidden xl:block">
        <img
          src={item.imagePcUrl}
          alt={item.title}
          className="max-h-[423px] w-full object-cover"
          onError={(e) => {
            console.error(
              'Desktop image failed to load:',
              '/' + item.imagePcUrl + ' ' + e,
            );
          }}
        />
      </div>
      {/* 모바일 배너 이미지 */}
      <div className="block h-full xl:hidden">
        <img
          src={item.imageMobileUrl}
          alt={item.title}
          className="max-h-[423px] w-full object-cover"
          onError={(e) => {
            console.error(
              'Mobile image failed to load:',
              '/' + item.imageMobileUrl + ' ' + e,
            );
          }}
        />
      </div>
    </div>
  );
};

export default AntdCarousel;
